import React, { useState, useEffect } from 'react';
import { RegexTesterService, RegexFlags } from '../../lib/services/regexTesterService';
import { EncodingOperation } from '../../lib/types/EncodingOperation';
import './RegExTester.css';

export const RegExTester: React.FC = () => {
  const [pattern, setPattern] = useState<string>('\\b\\w+@\\w+\\.\\w+\\b');
  const [testString, setTestString] = useState<string>('Contact us at support@example.com or sales@company.org for more information.');
  const [flags, setFlags] = useState<RegexFlags>({
    global: true,
    multiline: false,
    caseInsensitive: false,
    unicode: false,
    sticky: false,
    dotAll: false
  });
  const [result, setResult] = useState<EncodingOperation | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCheatSheet, setShowCheatSheet] = useState(false);

  // Process regex with debouncing
  useEffect(() => {
    const timer = setTimeout(() => {
      if (pattern && testString !== null && testString !== undefined) {
        processRegex();
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [pattern, testString, flags]);

  const processRegex = async () => {
    setIsProcessing(true);
    try {
      const operation = await RegexTesterService.test(pattern, testString, flags);
      setResult(operation);
    } catch (error) {
      console.error('Regex processing error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFlagToggle = (flag: keyof RegexFlags) => {
    setFlags(prev => ({
      ...prev,
      [flag]: !prev[flag]
    }));
  };

  const handleExampleClick = (examplePattern: string, exampleFlags: string, exampleTest?: string) => {
    setPattern(examplePattern);
    if (exampleTest) {
      setTestString(exampleTest);
    }

    // Parse flags
    const newFlags: RegexFlags = {
      global: exampleFlags.includes('g'),
      multiline: exampleFlags.includes('m'),
      caseInsensitive: exampleFlags.includes('i'),
      unicode: exampleFlags.includes('u'),
      sticky: exampleFlags.includes('y'),
      dotAll: exampleFlags.includes('s')
    };
    setFlags(newFlags);
  };

  const handleClear = () => {
    setPattern('');
    setTestString('');
    setResult(null);
  };

  const handleCopyResult = async () => {
    if (result?.outputData && typeof result.outputData === 'string') {
      try {
        await navigator.clipboard.writeText(result.outputData);
      } catch (error) {
        console.error('Failed to copy:', error);
      }
    }
  };

  const examples = RegexTesterService.getExamplePatterns();
  const flagDescriptions = RegexTesterService.getFlagDescriptions();

  // Highlight matches in test string
  const getHighlightedText = () => {
    if (!result || result.status === 'error' || !testString) {
      return testString;
    }

    try {
      const parsedResult = JSON.parse(result.outputData as string);
      if (!parsedResult.matches || parsedResult.matches.length === 0) {
        return testString;
      }

      const parts: React.ReactNode[] = [];
      let lastIndex = 0;

      parsedResult.matches.forEach((match: any, idx: number) => {
        // Add text before match
        if (match.index > lastIndex) {
          parts.push(
            <span key={`text-${idx}`}>
              {testString.substring(lastIndex, match.index)}
            </span>
          );
        }

        // Add highlighted match
        parts.push(
          <mark key={`match-${idx}`} className="regex-match">
            {match.match}
          </mark>
        );

        lastIndex = match.index + match.match.length;
      });

      // Add remaining text
      if (lastIndex < testString.length) {
        parts.push(
          <span key="text-end">
            {testString.substring(lastIndex)}
          </span>
        );
      }

      return parts;
    } catch (error) {
      return testString;
    }
  };

  return (
    <div className="regex-tester">
      <h1>Regular Expression Tester</h1>
      <p className="description">
        Test regular expressions with real-time matching, capture groups, and various flags.
      </p>

      <div className="regex-container">
        <div className="input-section">
          <div className="input-group">
            <label htmlFor="regex-pattern">Pattern</label>
            <input
              type="text"
              id="regex-pattern"
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              placeholder="Enter regex pattern..."
              spellCheck={false}
              aria-label="Regular expression pattern"
            />
          </div>

          <div className="flags-section">
            <label>Flags</label>
            <div className="flags-grid">
              {Object.entries(flagDescriptions).map(([flag, description]) => (
                <div key={flag} className="flag-item">
                  <label className="flag-label">
                    <input
                      type="checkbox"
                      checked={flags[flag as keyof RegexFlags] || false}
                      onChange={() => handleFlagToggle(flag as keyof RegexFlags)}
                      aria-label={`${flag}: ${description}`}
                    />
                    <span className="flag-name">{flag}</span>
                    <span className="flag-description">{description}</span>
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="input-group">
            <label htmlFor="test-string">Test String</label>
            <textarea
              id="test-string"
              value={testString}
              onChange={(e) => setTestString(e.target.value)}
              placeholder="Enter text to test against the regex..."
              rows={6}
              spellCheck={false}
              aria-label="Test string"
            />
          </div>

          <div className="button-group">
            <button
              className="btn-secondary"
              onClick={handleClear}
              type="button"
              aria-label="Clear all inputs"
            >
              Clear
            </button>
            <button
              className="btn-secondary"
              onClick={() => setShowCheatSheet(!showCheatSheet)}
              type="button"
              aria-label="Toggle cheat sheet"
              aria-expanded={showCheatSheet}
            >
              {showCheatSheet ? 'Hide' : 'Show'} Cheat Sheet
            </button>
          </div>
        </div>

        {showCheatSheet && (
          <div className="cheat-sheet" role="region" aria-label="Regex cheat sheet">
            <h3>Regular Expression Cheat Sheet</h3>
            <div className="examples-grid">
              {examples.map((example) => (
                <div key={example.name} className="example-item">
                  <button
                    className="example-button"
                    onClick={() => handleExampleClick(example.pattern, example.flags, example.testString)}
                    type="button"
                    aria-label={`Use example: ${example.name}`}
                  >
                    <div className="example-name">{example.name}</div>
                    <code className="example-query">
                      /{example.pattern}/{example.flags}
                    </code>
                    <div className="example-description">{example.description}</div>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="output-section">
          <div className="output-header">
            <label>Matches Preview</label>
            <div className="output-actions">
              {isProcessing && (
                <span className="processing-badge" aria-live="polite">
                  Processing...
                </span>
              )}
              {result?.status === 'success' && (
                <span className="match-count" aria-live="polite">
                  {result.metadata?.matchCount || 0} match{result.metadata?.matchCount !== 1 ? 'es' : ''}
                </span>
              )}
            </div>
          </div>

          <div
            className="matches-preview"
            aria-label="Matches preview with highlighting"
            aria-live="polite"
          >
            {result?.status === 'error' ? (
              <div className="error-message">{result.errorMessage}</div>
            ) : (
              <pre className="preview-text">{getHighlightedText()}</pre>
            )}
          </div>

          <div className="output-header">
            <label htmlFor="regex-result">Match Details (JSON)</label>
            <button
              className="btn-secondary btn-small"
              onClick={handleCopyResult}
              disabled={!result?.outputData}
              type="button"
              aria-label="Copy result to clipboard"
            >
              Copy
            </button>
          </div>

          <textarea
            id="regex-result"
            value={
              result?.status === 'error'
                ? result.errorMessage || 'An error occurred'
                : (result?.outputData as string) || ''
            }
            readOnly
            rows={10}
            placeholder="Match details will appear here..."
            className={result?.status === 'error' ? 'error' : ''}
            spellCheck={false}
            aria-label="Match details in JSON format"
          />

          {result?.metadata && result.status === 'success' && (
            <div className="metadata" aria-label="Regex metadata">
              <span>Pattern: <code>/{result.metadata.pattern}/{result.metadata.flags}</code></span>
              <span>Matches: {result.metadata.matchCount}</span>
              <span>Capture Groups: {result.metadata.captureGroupCount}</span>
              <span>Input: {result.metadata.inputLength} chars</span>
            </div>
          )}
        </div>
      </div>

      <noscript>
        <div className="noscript-warning">
          This tool requires JavaScript to be enabled for interactive regex testing.
        </div>
      </noscript>
    </div>
  );
};
